// The hours either side of kickoff, on hover.
//
// A single number on a fixture row answers "how warm", which is rarely the
// actual question. A 9am kickoff that reads 70° was 66° when you were setting up
// and is 81° with thunderstorms by the time you pack the car — that shape is the
// useful part, and it is already in the data: the pipeline stores six hourly
// periods around each kickoff.
//
// Fetched when the tooltip opens rather than with the fixture list. A schedule
// page shows up to 120 rows and almost none get hovered, so loading six points
// for every one of them would be a large payload nobody reads.
import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { tokens } from '../theme';
import { weatherGlyph } from '../utils/weather';
import { getHourlyWeather } from '../services/api';

/** "2026-08-29" → "Sat 29 Aug". Parsed as local, not UTC. */
function formatDay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

/** Drop a leading zero so the column stays narrow: "07:00" → "7". */
const hourLabel = (t) => String(Number((t || '').slice(0, 2)));

function Strip({ data }) {
  const hours = data.hourly || [];
  if (!hours.length) return null;

  return (
    <Box sx={{ px: 0.5, py: 0.25 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.75 }}>
        {formatDay(data.date)}
        {data.venue ? ` · ${data.venue}` : ''}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.25 }}>
        {hours.map((h) => {
          // The kickoff column is the one the row's own number came from, so it
          // is marked rather than left for the reader to work out.
          const isKickoff = h.t === data.kickoff;
          const { icon, label } = weatherGlyph(h.summary, h.day !== false);
          return (
            <Box
              key={h.t}
              sx={{
                textAlign: 'center', minWidth: 30,
                opacity: isKickoff ? 1 : 0.75,
                fontWeight: isKickoff ? 700 : 400,
              }}
            >
              <Box sx={{ fontSize: 10, letterSpacing: '0.02em' }}>{hourLabel(h.t)}</Box>
              <Box sx={{ fontSize: 15, lineHeight: 1.4 }} title={label}>{icon}</Box>
              <Box sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                {h.temp == null ? '–' : `${h.temp}°`}
              </Box>
              {/* Only worth the ink when rain is actually plausible. */}
              <Box sx={{ fontSize: 10, opacity: h.precip >= 20 ? 0.9 : 0 }}>
                {h.precip == null ? '' : `${h.precip}%`}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.7 }}>
        {hours.find((h) => h.t === data.kickoff)?.summary || ''}
        {data.lead_hours != null && ` · forecast ${Math.round(data.lead_hours / 24)}d out`}
      </Typography>
    </Box>
  );
}

export default function HourlyForecast({ matchId, fallback, children }) {
  const [data, setData] = useState(null);
  // idle → loading → done | none. 'none' covers both a 404 and a failure: either
  // way the plain text is what gets shown, so they need no distinction here.
  const [state, setState] = useState('idle');

  const load = useCallback(() => {
    if (state !== 'idle') return;
    setState('loading');
    getHourlyWeather(matchId)
      .then((d) => {
        setData(d);
        setState(d && d.hourly && d.hourly.length ? 'done' : 'none');
      })
      .catch(() => setState('none'));
  }, [matchId, state]);

  let title;
  if (state === 'done') title = <Strip data={data} />;
  else if (state === 'loading') {
    title = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={12} thickness={6} sx={{ color: 'inherit' }} />
        <Typography variant="caption">{fallback}</Typography>
      </Box>
    );
  } else title = fallback;

  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      onOpen={load}
      // Touch has no hover, so a tap opens it and it stays long enough to read.
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: tokens.navy,
            maxWidth: 'none',
            px: 1.25,
            py: 1,
          },
        },
        arrow: { sx: { color: tokens.navy } },
      }}
    >
      <Box component="span" sx={{ cursor: 'help' }}>{children}</Box>
    </Tooltip>
  );
}
